import { Alert, Grid, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { EEProps } from "./interfaces";
import SingleEmployeeProperty from "./SingleEmployeeProperty";
import { StyledTable } from "./styles";
import { useAppSelector } from "../../../../../custom-hooks/redux/redux";
import useAuthorization from "../../../../../custom-hooks/useAuthorization/useAuthorization";
import { EmployeeDTO, EmployeesFilter } from "../../../../../models/employees";
import AutocompleteDropdown from "../../../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { EmployeesApiService } from "../../../../../services/employees/employees-api.service";
import { AUTHORIZATIONS } from "../../../../../utils/constants/authorization-constants";

const AddEmployeeFromDatabase = ({
  selectedItem,
  selectedItemProperty,
  updateSelectedItem,
  updateSelectedItemProperty,
  getEEPropertyValue,
  selectedEmployeePropertyKey,
}: EEProps) => {
  const tRPEmployee = "pages.company_account_profile.tables.employees.fields";
  const tRPAddress = "pages.company_account_profile.tables.employee_address_mutations.fields";
  const tRP = "components.document_templates.prefill_and_generate_document_from_template";

  const { t } = useTranslation();

  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);
  const hasAdminAccess = useAuthorization([AUTHORIZATIONS.CompanyAccountsAccess.LegalEntityAdminAccess]);

  const authorizedLegalEntities = currentUserAuthorizations?.allowedDomainsPerLE
    ?.filter(
      (allowedDoamainPerLe) =>
        !!allowedDoamainPerLe.value?.find(
          (auth) =>
            auth === AUTHORIZATIONS.LegalEntityAccess.ProcessAccounting ||
            auth === AUTHORIZATIONS.LegalEntityAccess.ProcessPayroll
        )
    )
    ?.map((alloweDomainPerLE) => alloweDomainPerLE.key);

  const employeeProperties: {
    label: string;
    value: keyof EmployeeDTO;
    key?: keyof EmployeeDTO[keyof EmployeeDTO];
  }[] = [
    { label: `${t(`${tRPEmployee}.first_name`)}`, value: "firstName" },
    { label: `${t(`${tRPEmployee}.last_name`)}`, value: "lastName" },
    { label: `${t(`common_words.name`)}`, value: "name" },
    { label: `${t(`common_words.code`)}`, value: "employeeCode" },
    { label: `${t(`${tRPEmployee}.birth_date`)}`, value: "birthDate" },
    { label: `${t(`${tRPEmployee}.email`)}`, value: "email" },
    { label: `${t(`${tRPEmployee}.tax_id`)}`, value: "taxId" },
    { label: `${t(`${tRPEmployee}.department`)}`, value: "department" },
    {
      label: `${t(`${tRPAddress}.address_line_1`)}`,
      value: "address",
      key: "addressLine1" as keyof EmployeeDTO[keyof EmployeeDTO],
    },
    {
      label: `${t(`${tRPAddress}.address_line_2`)}`,
      value: "address",
      key: "addressLine2" as keyof EmployeeDTO[keyof EmployeeDTO],
    },
    {
      label: `${t(`${tRPAddress}.locality`)}`,
      value: "address",
      key: "locality" as keyof EmployeeDTO[keyof EmployeeDTO],
    },
    {
      label: `${t(`${tRPAddress}.region`)}`,
      value: "address",
      key: "region" as keyof EmployeeDTO[keyof EmployeeDTO],
    },
    {
      label: `${t(`${tRPAddress}.zip_code`)}`,
      value: "address",
      key: "zipCode" as keyof EmployeeDTO[keyof EmployeeDTO],
    },
  ];

  return (
    <Grid style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "10px 0px" }}>
      <AutocompleteDropdown
        field={"employee"}
        serviceApi={new EmployeesApiService()}
        handleChange={(_, value) => updateSelectedItem(value)}
        propsFilter={
          {
            legalEntityIds: hasAdminAccess ? [] : authorizedLegalEntities,
          } as EmployeesFilter
        }
        selectedValue={selectedItem}
      />

      {selectedItem ? (
        <>
          <Alert severity="info">{`${t(`${tRP}.selected_employee_hint`)}`} </Alert>
          <StyledTable>
            <TableHead>
              <TableRow>
                <TableCell align="center">{`${t(`${tRP}.employee_property`)}`}</TableCell>
                <TableCell align="center">{`${t(`${tRP}.property_value`)}`}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {employeeProperties.map((property, idx) => (
                <SingleEmployeeProperty
                  key={idx}
                  propertyLabel={property.label}
                  property={property.value}
                  propertyValue={getEEPropertyValue(property.value, property.key)}
                  updateSelectedItemProperty={updateSelectedItemProperty}
                  isSelected={selectedItemProperty === property.value && selectedEmployeePropertyKey === property.key}
                  propertyKey={property.key}
                />
              ))}
            </TableBody>
          </StyledTable>
        </>
      ) : (
        <></>
      )}
    </Grid>
  );
};

export default AddEmployeeFromDatabase;
